import { createContext, useContext, useState, useEffect, type ReactNode } from 'react'
import { type Lang, type Dict, translations } from '@/lib/i18n'

interface LanguageContextValue {
  lang: Lang
  t: Dict
  toggle: () => void
}

const LanguageContext = createContext<LanguageContextValue | null>(null)

const STORAGE_KEY = 'nexbit_admin_lang'

/** Read the persisted choice so the language survives a hard reload; default 'th'. */
function initialLang(): Lang {
  if (typeof window === 'undefined') return 'th'
  const stored = window.localStorage.getItem(STORAGE_KEY)
  return stored === 'en' || stored === 'th' ? stored : 'th'
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [lang, setLang] = useState<Lang>(initialLang)
  useEffect(() => {
    try { window.localStorage.setItem(STORAGE_KEY, lang) } catch { /* ignore */ }
  }, [lang])
  const toggle = () => setLang((l) => (l === 'th' ? 'en' : 'th'))
  return (
    <LanguageContext.Provider value={{ lang, t: translations[lang], toggle }}>
      {children}
    </LanguageContext.Provider>
  )
}

export function useT(): Dict {
  const ctx = useContext(LanguageContext)
  if (!ctx) throw new Error('useT must be used inside LanguageProvider')
  return ctx.t
}

export function useLang(): { lang: Lang; toggle: () => void } {
  const ctx = useContext(LanguageContext)
  if (!ctx) throw new Error('useLang must be used inside LanguageProvider')
  return { lang: ctx.lang, toggle: ctx.toggle }
}
